import type { BenchmarkStats } from './benchmark-utils'

/**
 * One distribution strip (spec 2026-09-13-01, the building view).
 *
 * The empirical distribution, never a fitted curve: costs are right-skewed and
 * cohorts small, so a smooth bell would lie about the tail it is meant to show
 * and hide `n`. Each peer is a dot — `n` can be counted. Band p10–p90, a median
 * tick, the alert fence dashed, the subject on top.
 *
 * Plain SVG rather than ECharts: a strip is 44 px tall, a building carries a
 * dozen, and ECharts would mount a canvas per line.
 */
interface Props {
    value: number | null
    stats: BenchmarkStats | null
    peerValues: number[]
    outlier: string | null
    label: string
    formatValue: (v: number) => string
    testId: string
}

const HEIGHT = 44
const MID = HEIGHT / 2
// Horizontal padding, in percent of the width: coordinates are percentages so
// the dots stay round at every width.
const PAD = 2

export function DistributionStrip({ value, stats, peerValues, outlier, label, formatValue, testId }: Props) {
    const domain: number[] = [...peerValues]
    if (value !== null) domain.push(value)
    if (stats) {
        domain.push(stats.p10, stats.p90)
        if (stats.fence_available && stats.fence_high !== undefined) domain.push(stats.fence_high)
    }
    if (domain.length === 0) {
        return <div className="h-11" data-testid={testId} />
    }
    let lo = Math.min(...domain)
    let hi = Math.max(...domain)
    if (hi === lo) {
        lo -= Math.abs(lo) * 0.1 || 1
        hi += Math.abs(hi) * 0.1 || 1
    }
    const span = hi - lo
    lo -= span * 0.06
    hi += span * 0.06
    const pct = (v: number) => PAD + ((v - lo) / (hi - lo)) * (100 - 2 * PAD)
    const x = (v: number) => `${pct(v)}%`

    const subjectColor = outlier === 'high' ? 'var(--warning)' : 'var(--primary)'

    return (
        <svg
            className="h-11 w-full overflow-visible"
            role="img"
            aria-label={label}
            data-testid={testId}
        >
            {stats && (
                <rect
                    x={x(stats.p10)}
                    y={MID - 9}
                    width={`${Math.max(0.3, pct(stats.p90) - pct(stats.p10))}%`}
                    height={18}
                    rx={3}
                    style={{ fill: 'var(--muted)' }}
                    data-testid={`${testId}-band`}
                >
                    <title>{`p10 ${formatValue(stats.p10)} — p90 ${formatValue(stats.p90)}`}</title>
                </rect>
            )}
            {peerValues.map((v, i) => (
                <circle
                    key={i}
                    cx={x(v)}
                    cy={MID}
                    r={3.5}
                    style={{ fill: 'var(--muted-foreground)', opacity: 0.45 }}
                >
                    <title>{formatValue(v)}</title>
                </circle>
            ))}
            {stats && (
                <line
                    x1={x(stats.p50)}
                    x2={x(stats.p50)}
                    y1={MID - 12}
                    y2={MID + 12}
                    style={{ stroke: 'var(--foreground)', strokeWidth: 2, opacity: 0.6 }}
                >
                    <title>{`p50 ${formatValue(stats.p50)}`}</title>
                </line>
            )}
            {stats?.fence_available && stats.fence_high !== undefined && (
                <line
                    x1={x(stats.fence_high)}
                    x2={x(stats.fence_high)}
                    y1={MID - 14}
                    y2={MID + 14}
                    style={{ stroke: 'var(--warning)', strokeWidth: 1.5, strokeDasharray: '3 3' }}
                    data-testid={`${testId}-fence`}
                />
            )}
            {value !== null && (
                <circle
                    cx={x(value)}
                    cy={MID}
                    r={6.5}
                    style={{ fill: subjectColor, stroke: 'var(--background)', strokeWidth: 2 }}
                    data-testid={`${testId}-subject`}
                >
                    <title>{formatValue(value)}</title>
                </circle>
            )}
        </svg>
    )
}
